import { BoundingBox } from 'src/features/review/constants';
import { Document, WikiDocument, isWikiDocument } from 'src/lib/types';

// Document type returned by the API
export type DocumentType = 'wiki' | 'document';

// Unified document response interface that can hold either type
export interface DocumentWithExtras {
  sas_link: string | null;
  num_likes: number;
  user_liked: boolean;
  document: WikiDocument | Document;
  document_type: DocumentType;
  bounding_boxes: BoundingBox[];
  source_type?: string | null;
  created_by_name?: string | null;
  /** Set when the viewing user is not the owner and access was granted via a group. */
  shared_via_group_name?: string | null;
  shared_via_group_id?: string | null;
  /** Groups the viewer belongs to where this document is linked. */
  linked_groups?: { id: string; name: string }[];
  /** Present only when the viewer can open the source chat. */
  canvas_origin?: {
    session_id: string;
    canvas_id: string;
    canvas_version_id: number;
    title: string;
    is_latest: boolean;
  } | null;
  /** Effective permission level the requesting user has on this document (ResourcePermissionRole int). */
  user_permission?: number | null;
  /** Set for image documents — drives the image viewer instead of the PDF viewer. */
  mime_type?: string | null;
  /** False only when neither the original nor a rendition can render (old HEIC, no JPEG). */
  browser_renderable?: boolean;
  /** Browser-renderable JPEG rendition, tried only when `sas_link` (e.g. HEIC) fails to decode. */
  fallback_sas_link?: string | null;
  /**
   * "What changed" audit trail across every approved scraped update to this
   * document's full version history, newest first. Only populated when the
   * `document_change_summary` feature flag is enabled for the requesting
   * user; empty/undefined otherwise (including when there's no AI-summarised
   * update yet).
   */
  change_history?: ChangeHistoryEntry[];
}

export interface ChangeHistoryEntry {
  summary: string;
  changed_at: string;
}

// Type guard to check if the response is for a wiki document.
// Both conditions are required: document_type 'wiki' identifies the stored type,
// and tenant 'public' distinguishes genuine public-wiki docs from org documents
// that were migrated from the wiki but retain the 'wiki' document_type.
export function isWikiDocumentResponse(
  response: DocumentWithExtras
): response is DocumentWithExtras & { document: WikiDocument; document_type: 'wiki' } {
  return (
    response.document_type === 'wiki' && response.document?.tenant === 'public' && isWikiDocument(response.document)
  );
}
